// Supabase auth gate.
//
// Phase 2A (hardened): every visitor must sign in with their email. Role
// and scope are decided server-side via the ensure_profile() RPC. The client
// only proposes a tree-card match, which the RPC clamps to editor/viewer.
// Admin assignment must come from Supabase-side state, an existing admin
// profile, or the very-first-user bootstrap.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { supabase, supabaseConfigured } from '../config/supabaseClient';

const MAIN_STORAGE_KEY = 'gia-pha-tree-v2';
const PORTAL_TREE_PREFIX = 'gia-pha-tree-portal-';

// TEMPORARILY un-gated for production debugging. Once the Railway sign-in
// flow is verified end-to-end, switch back to:
//   const devLog = process.env.NODE_ENV === 'development' ? ... : () => {};
const devLog = (...args) => console.log(...args);

// Walk every locally-stored tree looking for a person whose auth.email
// matches the signed-in user.
function findPersonInLocalTreesByEmail(email) {
  const target = (email || '').trim().toLowerCase();
  if (!target) return null;
  const keys = [MAIN_STORAGE_KEY];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PORTAL_TREE_PREFIX)) keys.push(k);
  }
  for (const storageKey of keys) {
    const raw = localStorage.getItem(storageKey);
    if (!raw) continue;
    let tree;
    try { tree = JSON.parse(raw); } catch (e) { continue; }
    const found = walkPersonForEmail(tree, target);
    if (found) return { person: found, storageKey };
  }
  return null;
}

function walkPersonForEmail(person, target) {
  if (!person) return null;
  if (person.auth?.email?.toLowerCase() === target) return person;
  for (const m of person.marriages || []) {
    const inSpouse = walkPersonForEmail(m.spouse, target);
    if (inSpouse) return inSpouse;
    for (const c of m.children || []) {
      const inChild = walkPersonForEmail(c, target);
      if (inChild) return inChild;
    }
  }
  return null;
}

// Server-side fallback for invitees on a fresh device whose localStorage is
// empty: query Supabase trees for any tree containing this email.
async function findPersonInRemoteTreesByEmail(email) {
  const target = (email || '').trim().toLowerCase();
  if (!target) return null;
  const { data, error } = await supabase
    .from('trees')
    .select('storage_key, data');
  if (error || !data) return null;
  for (const row of data) {
    const found = walkPersonForEmail(row.data, target);
    if (found) return { person: found, storageKey: row.storage_key };
  }
  return null;
}

const AuthContext = createContext({
  user: null,
  profile: null,
  loading: true,
  notInvited: false,
  profileError: null,
  recoveryMode: false,
  clearRecoveryMode: () => {},
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notInvited, setNotInvited] = useState(false);
  // Set when ensure_profile() failed for a reason other than not_invited
  // (network error, RLS misconfig, server error). The AppGate uses this to
  // show a blocking error screen instead of dropping the user into the tree
  // with no profile (which would silently grant unauthenticated read-only
  // access).
  const [profileError, setProfileError] = useState(null);
  // Set when PASSWORD_RECOVERY fires (user clicked a reset or invite email).
  // The AppGate forces a "set new password" form until cleared.
  const [recoveryMode, setRecoveryMode] = useState(false);
  const ensuringFor = useRef(null);
  // Set true when our signOut() is called — we only react to SDK SIGNED_OUT
  // events when we asked for them. Spurious SDK SIGNED_OUT events fire
  // mid-sign-in (observed: between tree-match query and RPC return) and the
  // earlier "session-still-active" guard couldn't catch them because the
  // SDK had already cleared localStorage as part of the spurious event.
  const userSignedOutRef = useRef(false);

  // Materialize the profile via the ensure_profile RPC. Returns the profile
  // row on success or null on a soft failure (caller still proceeds with
  // setLoading(false), but a hard failure sets profileError so the gate
  // blocks the tree).
  const ensureProfile = useCallback(async (authUser) => {
    if (!authUser) return null;
    if (ensuringFor.current?.id === authUser.id) {
      return ensuringFor.current.promise;
    }

    const promise = (async () => {
      const email = (authUser.email || '').toLowerCase();
      devLog('[gia-pha] ensureProfile start; email:', email);

      // Find a matching person card on a tree, if any. Both lookups are
      // best-effort; the RPC also handles the no-match case.
      const localMatch = findPersonInLocalTreesByEmail(email);
      const remoteMatch = localMatch ? null : await findPersonInRemoteTreesByEmail(email);
      const match = localMatch || remoteMatch;
      devLog('[gia-pha] tree match:', match ? `${match.person.name} on ${match.storageKey}` : 'none');

      const auth = match?.person?.auth || {};
      // Only forward editor/viewer to the RPC. The RPC rejects 'admin' from
      // p_match_role, so even if a malicious client lies, they can't promote
      // themselves through this path.
      const matchRole =
        auth.role === 'editor' || auth.role === 'viewer' ? auth.role : null;
      const matchScope =
        matchRole === 'editor' &&
        (auth.scope === 'branch' || auth.scope === 'tree' || auth.scope === 'global')
          ? auth.scope
          : null;

      const { data, error } = await supabase.rpc('ensure_profile', {
        p_email: email,
        p_match_role: matchRole,
        p_match_scope: matchScope,
        p_match_person_id: match?.person?.id || null,
        p_match_tree_storage_key: match?.storageKey || null,
      });

      if (error) {
        // Postgres raise messages come back in error.message. The RPC raises
        // 'not_invited' to mean "we found nothing and they're not allowed".
        const msg = error.message || '';
        if (msg.includes('not_invited')) {
          devLog('[gia-pha] not_invited from RPC — signing out');
          userSignedOutRef.current = true;
          await supabase.auth.signOut();
          setUser(null);
          setProfile(null);
          setNotInvited(true);
          return null;
        }
        console.warn('[gia-pha] ensure_profile failed:', error.message || error);
        setProfileError(error.message || String(error));
        return null;
      }

      // RPC returns a profiles row directly (LANGUAGE plpgsql, RETURNS profiles).
      const row = Array.isArray(data) ? data[0] : data;
      devLog('[gia-pha] RPC raw data:', data);
      devLog('[gia-pha] profile materialized; role:', row?.role);
      if (!row) {
        console.warn('[gia-pha] ensure_profile returned no row — unexpected');
        setProfileError('ensure_profile returned no profile row');
        return null;
      }
      setProfile(row);
      setProfileError(null);
      return row;
    })();

    ensuringFor.current = { id: authUser.id, promise };
    try {
      return await promise;
    } finally {
      if (ensuringFor.current?.promise === promise) {
        ensuringFor.current = null;
      }
    }
  }, []);

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      const withTimeout = (p, ms, label) =>
        Promise.race([
          p,
          new Promise((_, reject) =>
            setTimeout(
              () => reject(new Error(`${label} timed out after ${ms}ms`)),
              ms
            )
          ),
        ]);
      try {
        const { data } = await withTimeout(
          supabase.auth.getSession(),
          5000,
          'getSession'
        );
        if (cancelled) return;
        const u = data?.session?.user || null;
        setUser(u);
        if (u) await withTimeout(ensureProfile(u), 8000, 'ensureProfile');
      } catch (e) {
        console.warn('[gia-pha] auth boot failed:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange(async (_evt, sess) => {
      const u = sess?.user || null;
      devLog('[gia-pha] auth state change:', _evt, '— user:', u?.email || 'null');

      // PASSWORD_RECOVERY = user clicked a reset/invite link. They have a
      // temporary session — gate them to the set-password form before
      // letting them touch the tree.
      if (_evt === 'PASSWORD_RECOVERY') {
        setRecoveryMode(true);
        setUser(u);
        return;
      }

      if (u) {
        setUser(u);
        setNotInvited(false);
        setProfileError(null);
        await ensureProfile(u);
      } else if (!userSignedOutRef.current) {
        // SDK fired SIGNED_OUT but we didn't ask for it. This is the
        // spurious mid-sign-in event observed in the SDK. The JWT is still
        // valid for in-flight requests — keep the React state intact and
        // let the in-progress ensureProfile finish without interference.
        devLog('[gia-pha] non-user-initiated SIGNED_OUT — ignoring');
        return;
      } else {
        // User-initiated signout: clear state unconditionally. We do NOT
        // recheck getSession here — the SDK can lag in clearing localStorage,
        // so getSession may still return the old session for a brief
        // window, and re-applying it would undo the signout the user just
        // asked for. The Sign Out button used to need a refresh because of
        // exactly that race.
        userSignedOutRef.current = false;
        setUser(null);
        setProfile(null);
      }
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, [ensureProfile]);

  const signOut = useCallback(async () => {
    if (!supabaseConfigured) return;
    // Mark this signout as user-initiated so the onAuthStateChange handler
    // actually clears state when the SDK fires SIGNED_OUT in response.
    userSignedOutRef.current = true;
    // Clear React state synchronously so the UI transitions to LoginPage on
    // the next paint — don't wait for the SDK round-trip. On a slow network
    // the SDK call can take several seconds, during which the button looked
    // unresponsive and the user would click repeatedly.
    setUser(null);
    setProfile(null);
    setNotInvited(false);
    setProfileError(null);
    setRecoveryMode(false);
    // Fire-and-forget the SDK call; failures here just mean Supabase still
    // has a session cookie on its side and the next getSession() may revive
    // it, which is fine — the user can sign out again.
    supabase.auth.signOut().catch((e) => {
      console.warn('[gia-pha] supabase signOut failed:', e);
    });
  }, []);

  // Called by SetPasswordPage when the user finishes setting their new
  // password. Drops them back into the normal sign-in flow.
  const clearRecoveryMode = useCallback(async () => {
    setRecoveryMode(false);
    // After a fresh password is set the user is fully signed in — kick
    // ensureProfile so the gate transitions to the tree without requiring
    // another reload.
    const { data } = await supabase.auth.getSession();
    const u = data?.session?.user || null;
    if (u) {
      setUser(u);
      await ensureProfile(u);
    }
  }, [ensureProfile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        notInvited,
        profileError,
        recoveryMode,
        clearRecoveryMode,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
